/* eslint-disable import/no-extraneous-dependencies, no-useless-escape */
import { APIConnectionRecord } from '@tassat/payments-definitions';
import { ActionTypes } from '@tassat/ui-definitions';

import { IErrorValues, IFormValues } from './types';
import {
    textFieldCharacterWhitelist,
    validateBasicURL,
    validateDate,
    validateEmailFormatting,
    validateLengthRequirement,
    validateNoExcelFormulaStartingCharacters,
    validateNoNonPrintable,
    validateNoWhitespace,
    validateRequiredStrict
} from '../helpers/validationHelpers';

export const validateApiConnection = (values: IFormValues, initialValues: IFormValues, opts: {
    actionType: ActionTypes;
    audiencePrefix: string;
    apiConnections: APIConnectionRecord[];
}) => {
    const {
        actionType,
        audiencePrefix,
        apiConnections
    } = opts;

    const errors: IErrorValues = {} as IErrorValues;

    const connectionName = values.APIConnectionName.trim();
    const contactName = values.APIFirmContactName.trim();
    const supportPhone = values.APIFirmPhone.trim();
    const notificationEmail = values.APIFirmEmail.trim();
    const audience = values.Audience.trim();
    const sourceId = values.SourceId.trim();
    const effectiveDate = values.EffectiveDate.trim();

    if (!connectionName) {
        errors.APIConnectionName = 'Required';
    } else if (
        (
            (actionType === ActionTypes.edit && connectionName.toLowerCase() !== initialValues.APIConnectionName.toLowerCase()) ||
            actionType === ActionTypes.add
        ) &&
        apiConnections.find((api) => api.APIConnectionName.trim()
            .toLowerCase() === connectionName.toLowerCase())) {
        errors.APIConnectionName = 'An API with this name already exists, please enter a unique name';
    }
    const apiConnectionNameFormulaError = validateNoExcelFormulaStartingCharacters(connectionName);
    const apiConnectionNameWhitelistError = textFieldCharacterWhitelist(connectionName);
    if (apiConnectionNameWhitelistError) {
        errors.APIConnectionName = apiConnectionNameWhitelistError;
    } else if (apiConnectionNameFormulaError) {
        errors.APIConnectionName = apiConnectionNameFormulaError;
    }

    if (!contactName) {
        errors.APIFirmContactName = 'Required';
    }
    const apiFirmContactFormulaError = validateNoExcelFormulaStartingCharacters(contactName);
    const contactNameWhitelistError = textFieldCharacterWhitelist(contactName);

    if (contactNameWhitelistError) {
        errors.APIFirmContactName = contactNameWhitelistError;
    } else if (apiFirmContactFormulaError) {
        errors.APIFirmContactName = apiFirmContactFormulaError;
    }

    const apiFirmEmailError = validateRequiredStrict(notificationEmail);
    const apiFirmEmailFormatError = validateEmailFormatting(notificationEmail);
    const apiFirmEmailFormulaError = validateNoExcelFormulaStartingCharacters(notificationEmail);
    if (apiFirmEmailError) {
        errors.APIFirmEmail = apiFirmEmailError;
    } else if (apiFirmEmailFormatError) {
        errors.APIFirmEmail = apiFirmEmailFormatError;
    } else if (apiFirmEmailFormulaError) {
        errors.APIFirmEmail = apiFirmEmailFormulaError;
    }

    if (!supportPhone) {
        errors.APIFirmPhone = 'Required';
    }
    const apiFirmPhoneLengthError = validateLengthRequirement(supportPhone, 32);
    const apiFirmPhoneFormulaError = validateNoExcelFormulaStartingCharacters(supportPhone);
    const apiFirmPhoneWhitelistError = textFieldCharacterWhitelist(supportPhone);

    if (apiFirmPhoneLengthError) {
        errors.APIFirmPhone = apiFirmPhoneLengthError;
    } else if (apiFirmPhoneWhitelistError) {
        errors.APIFirmPhone = apiFirmPhoneWhitelistError;
    } else if (apiFirmPhoneFormulaError) {
        errors.APIFirmPhone = apiFirmPhoneFormulaError;
    }

    if (actionType !== ActionTypes.edit) {
        if (!sourceId) {
            errors.SourceId = 'Required';
        } else if (/[,"'\(\)]/g.test(sourceId)) {
            errors.SourceId = 'Source ID can not contain commas, quotes or parentheses';
        }

        const sourceIdWhitespaceError = validateNoWhitespace(sourceId);
        const sourceIdFormulaError = validateNoExcelFormulaStartingCharacters(sourceId);
        const sourceIdWhitelistError = textFieldCharacterWhitelist(sourceId);

        if (sourceIdWhitelistError) {
            errors.SourceId = sourceIdWhitelistError;
        } else if (sourceIdWhitespaceError) {
            errors.SourceId = sourceIdWhitespaceError;
        } else if (sourceIdFormulaError) {
            errors.SourceId = sourceIdFormulaError;
        } else if (apiConnections.find((api) => api.SourceId === values.SourceId)) {
            errors.SourceId = 'An API with this source ID already exists, please enter a unique source ID';
        }

        const audienceWhitespaceError = validateNoWhitespace(audience);
        const audienceUrlError = validateBasicURL(audience);
        const audienceFormulaError = validateNoExcelFormulaStartingCharacters(audience);
        const audienceNonPrintableError = validateNoNonPrintable(audience);
        if (!audience) {
            errors.Audience = 'Required';
        } else if (audienceWhitespaceError) {
            errors.Audience = audienceWhitespaceError;
        } else if (audienceUrlError) {
            errors.Audience = audienceUrlError;
        } else if (audienceFormulaError) {
            errors.Audience = audienceFormulaError;
        } else if (audienceNonPrintableError) {
            errors.Audience = audienceNonPrintableError;
        } else if (apiConnections.find((api) => api.Audience === audiencePrefix + values.Audience)) {
            errors.Audience = 'An API with this audience already exists, please enter a unique audience';
        }
    }

    const dateError = validateRequiredStrict(effectiveDate);
    const dateFormatError = validateDate(effectiveDate);
    if (dateError) {
        errors.EffectiveDate = dateError;
    } else if (dateFormatError) {
        errors.EffectiveDate = dateFormatError;
    }

    return errors;
};